"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get ArtifactChatMessage () {
        return ArtifactChatMessage;
    },
    get ArtifactOutput () {
        return ArtifactOutput;
    },
    get ArtifactReportQuery () {
        return ArtifactReportQuery;
    },
    get ReportingArtifactDocument () {
        return ReportingArtifactDocument;
    }
});
const _bson = require("bson");
const _zod = require("zod");
const _utilsisomorphic = require("@performyard/utils-isomorphic");
const _enums = require("./enums");
// columns/sorts carry the agent/artifact columnId-form shape (reporting-chat tools.ts,
// run-report-query.ts) — NOT the pipeline's internal ref-form ({type, name}), which
// run-report-query.ts translates columnId into at execution time. reportType uses that same agent
// namespace ("goalReport"|"reviewReport"|"surveyReport"|"employeeReport") so an emit frame's
// query saves into an artifact and re-runs through runReportQuery with zero translation.
// summarizeBy/aggregationOperations store the agent's columnId-form intent — the shared runner
// (run-report-query.ts) translates columnId → ref at execution time, so re-running a saved artifact
// re-derives the same ref-form the pipeline expects. Models cannot import service-contracts, so
// these are redefined here — kept permissive/passthrough (sorts excepted, see below) since strict
// validation otherwise happens at the internal-api boundary, not storage.
// Bare registry-id strings are accepted too — chart saves send params.columns as plain ids.
// As loose as the chat tool's QueryParamsSchema so every emitted shape saves — runReportQuery
// re-validates at render time, which is the real guard.
const ArtifactReportColumn = _zod.z.union([
    _zod.z.string(),
    _zod.z.record(_zod.z.string(), _zod.z.unknown())
]);
// Mirrors ReportSort (service-contracts/src/reporting-chat/request.ts) — every pipeline sort
// translator dereferences direction/fieldPath unguarded, so both must be validated here too.
const ArtifactReportSort = _zod.z.object({
    name: _zod.z.string(),
    direction: _zod.z.enum([
        "asc",
        "desc"
    ]),
    fieldPath: _zod.z.array(_zod.z.string()).min(1),
    type: _zod.z.string().optional()
}).passthrough();
const ArtifactReportSummarizeBy = _zod.z.object({
    columnId: _zod.z.string(),
    selection: _zod.z.union([
        _zod.z.string(),
        _zod.z.number(),
        _zod.z.null()
    ]).optional()
}).passthrough();
// Restricted to the 5 executable aggregation operations runReportQuery's allowedAggregationsForColumn
// actually recognizes (same vocabulary as tools.ts's AggregationOperationInputSchema) — otherwise an
// unrecognized value like "median" persists here and only 422s later, on reopen, via runReportQuery.
// Models can't import service-contracts anyway (it depends on models, so the reverse import would
// cycle). NOT models/enums.ts's SummaryFunction either: that's the legacy ref-form reporting
// vocabulary (7 values, incl. "concatenate"/"none"), a distinct concept from this columnId-form
// executable set — reusing it would let "concatenate"/"none" back through and reproduce this same
// bug for those two values.
const ArtifactAggregationOperation = _zod.z.enum([
    "average",
    "sum",
    "minimum",
    "maximum",
    "count"
]);
const ArtifactReportAggregationOperation = _zod.z.object({
    columnId: _zod.z.string(),
    operation: ArtifactAggregationOperation
}).passthrough();
// Mirrors AgentFieldFilterInput (dto/service-actions/reporting/translate-filter.ts) — a registry
// columnId plus an optional comparison and the raw value to match against. `comparison` is
// restricted to a real FilterComparison member: previously this was `z.record(z.string(),
// z.unknown())`, so a made-up/typo'd comparison (e.g. "invalid") persisted, and
// translateReportFilters's omitted-vs-unrecognized fallback treated it identically to an omitted
// one — silently equality-filtering instead of the comparison actually requested. `value` stays
// unknown; its valid shape depends on the column's field type, which only the registry (checked at
// render time via validateReportQueryRefs) can confirm.
const ArtifactReportFilterEntry = _zod.z.object({
    columnId: _zod.z.string(),
    comparison: _enums.FilterComparisonEnum.nullish(),
    value: _zod.z.unknown()
}).passthrough();
const ArtifactReportFilters = _zod.z.array(_zod.z.array(ArtifactReportFilterEntry)).nullable();
// Agent's columnId-form query intent, translated to ref-form by the shared runner at execution time.
const artifactReportParams = {
    columns: _zod.z.array(ArtifactReportColumn).nullish(),
    filters: ArtifactReportFilters.optional(),
    sorts: _zod.z.array(ArtifactReportSort).nullish(),
    summarizeBy: _zod.z.array(ArtifactReportSummarizeBy).optional(),
    aggregationOperations: _zod.z.array(ArtifactReportAggregationOperation).optional(),
    pageNumber: _zod.z.number().int().positive().optional(),
    pageSize: _zod.z.number().int().positive().optional()
};
const ArtifactReportQuery = _zod.z.discriminatedUnion("reportType", [
    _zod.z.object({
        reportType: _zod.z.literal("reviewReport"),
        // runReportQuery self-creates a fresh session per execution; the stored value is legacy/unused.
        questionGroupingSessionID: _utilsisomorphic.transformObjectId.nullish(),
        ...artifactReportParams
    }),
    _zod.z.object({
        reportType: _zod.z.literal("surveyReport"),
        questionGroupingSessionID: _utilsisomorphic.transformObjectId.nullish(),
        ...artifactReportParams
    }),
    _zod.z.object({
        reportType: _zod.z.literal("goalReport"),
        ...artifactReportParams
    }),
    _zod.z.object({
        reportType: _zod.z.literal("employeeReport"),
        ...artifactReportParams
    })
]);
const ArtifactOutput = _zod.z.object({
    title: _zod.z.string(),
    chartType: _zod.z.enum([
        "bar",
        "line",
        "pie"
    ]).optional(),
    categoryField: _zod.z.string().optional(),
    valueField: _zod.z.string().optional(),
    answerText: _zod.z.string().optional(),
    underlyingQuery: _zod.z.string().optional()
});
const ArtifactChatMessage = _zod.z.object({
    role: _zod.z.enum([
        "user",
        "assistant"
    ]),
    content: _zod.z.string()
});
const ReportingArtifactDocument = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    company_id: _zod.z.instanceof(_bson.ObjectId),
    name: _zod.z.string(),
    // Artifacts are always chat-created — no reporting-UI write path exists for this collection.
    created_via: _zod.z.literal("ai_chat"),
    createdBy: _zod.z.instanceof(_bson.ObjectId).or(_zod.z.string()).nullish(),
    outputType: _zod.z.enum([
        "answer",
        "table",
        "chart"
    ]),
    // Optional: "answer" outputs are text snapshots with no structured re-runnable query;
    // table/chart artifacts carry a query so reopening can re-run it and re-shape identically.
    query: ArtifactReportQuery.optional(),
    output: ArtifactOutput,
    // Optional: chat-history saving was deferred post-prototype — new artifacts omit it entirely.
    chatHistory: _zod.z.array(ArtifactChatMessage).optional(),
    createdAt: _zod.z.date(),
    updatedAt: _zod.z.date(),
    deletedAt: _zod.z.date().nullable().optional()
});
